import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/* ================================================================== */
/* Helpers                                                            */
/* ================================================================== */

const keyOf = (a, b) => {
  const x = Math.min(Number(a), Number(b));
  const y = Math.max(Number(a), Number(b));
  return `${x}_${y}`;
};
const parseKey = (k) => k.split("_").map(Number);

const symDiff = (a, b) => {
  let n = 0;
  a.forEach((k) => { if (!b.has(k)) n++; });
  b.forEach((k) => { if (!a.has(k)) n++; });
  return n;
};

const esEliminar = (acc) => /elim|borr|quit/i.test(acc || "");
const esActualizar = (acc) => /actualiz|nivel|super/i.test(acc || "");
const esCrear = (acc) => !esEliminar(acc) && !esActualizar(acc);

// Colores base (relleno por nº de enlaces, "como antes")
const GRIS = { fill: "#9ca3af", borde: "#6b7280", texto: "#ffffff" };
const ROJO = { fill: "#ef4444", borde: "#b91c1c", texto: "#ffffff" };
const VERDES = [
  null,
  { fill: "#86efac", borde: "#22c55e", texto: "#14532d" }, // 1 enlace
  { fill: "#22c55e", borde: "#16a34a", texto: "#ffffff" }, // 2 enlaces
  { fill: "#15803d", borde: "#166534", texto: "#ffffff" }, // 3 enlaces
];
const VERDE_MAX = { fill: "#14532d", borde: "#0f3d22", texto: "#ffffff" }; // 4+

const colorNodo = (grado, hayEnlaces) => {
  if (!hayEnlaces) return GRIS;
  if (grado <= 0) return ROJO;
  if (grado >= 4) return VERDE_MAX;
  return VERDES[grado];
};

// Borde grueso por grupo (solo cuando hay 2+ grupos separados)
const GRUPO_BORDE = ["#f97316", "#eab308", "#3b82f6", "#8b5cf6", "#06b6d4", "#ec4899"];
// Dorado del superlink
const ORO = { linea: "#fbbf24", borde: "#b45309", aro: "#f59e0b", circulo: "#f59e0b" };

// Componentes conexas (grupos) ordenadas por tamaño desc y luego por id.
function computeClusters(alumnos, links) {
  const ids = alumnos.map((a) => Number(a.id));
  const adj = {};
  ids.forEach((id) => { adj[id] = []; });
  links.forEach((k) => {
    const [a, b] = parseKey(k);
    if (adj[a] && adj[b]) { adj[a].push(b); adj[b].push(a); }
  });
  const seen = new Set();
  const comps = [];
  ids.forEach((id) => {
    if (seen.has(id)) return;
    const comp = [];
    const stack = [id];
    seen.add(id);
    while (stack.length) {
      const cur = stack.pop();
      comp.push(cur);
      adj[cur].forEach((nb) => { if (!seen.has(nb)) { seen.add(nb); stack.push(nb); } });
    }
    comp.sort((x, y) => x - y);
    comps.push(comp);
  });
  comps.sort((A, B) => B.length - A.length || A[0] - B[0]);
  return comps;
}

const radioNodo = (n) => (n > 12 ? 26 : n > 8 ? 30 : n > 5 ? 36 : 42);
const inicial = (nombre) => (nombre ? nombre.trim()[0].toUpperCase() : "?");
const corto = (s, n = 12) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "—");

/* ================================================================== */
/* Anillo (SVG) con grupos, separadores y superlink                   */
/* ================================================================== */

const Anillo = ({
  alumnos, links, supers = new Set(), selectedId = null,
  onNodeClick = null, onEdgeClick = null, resaltarNuevos = null, ajustar = false,
}) => {
  const SIZE = 460;
  const n = alumnos.length;
  const nodeR = radioNodo(n);
  const ringR = SIZE / 2 - nodeR - 52;
  const cx = SIZE / 2, cy = SIZE / 2;

  const grados = {};
  alumnos.forEach((a) => { grados[Number(a.id)] = 0; });
  links.forEach((k) => {
    const [a, b] = parseKey(k);
    if (grados[a] !== undefined) grados[a]++;
    if (grados[b] !== undefined) grados[b]++;
  });
  const hayEnlaces = links.size > 0;

  // Nodos que participan en algún superlink
  const superNodos = new Set();
  supers.forEach((k) => { if (links.has(k)) { const [a, b] = parseKey(k); superNodos.add(a); superNodos.add(b); } });

  const comps = useMemo(() => computeClusters(alumnos, links), [alumnos, links]);

  // Borde de grupo: solo si hay 2+ grupos (componentes de tamaño >= 2)
  const multiCount = comps.filter((c) => c.length >= 2).length;
  const grupoBorde = {};
  if (multiCount >= 2) {
    let gi = 0;
    comps.forEach((comp) => {
      if (comp.length >= 2) { const col = GRUPO_BORDE[gi % GRUPO_BORDE.length]; gi++; comp.forEach((id) => { grupoBorde[id] = col; }); }
    });
  }

  // Layout en anillo con huecos + separadores cuando hay 2+ componentes
  const useGaps = comps.length >= 2;
  const slots = [];
  comps.forEach((comp) => {
    comp.forEach((id) => slots.push({ node: id }));
    if (useGaps) slots.push({ gap: true });
  });
  const m = slots.length || 1;
  const start = -Math.PI / 2;
  const pos = {};
  const seps = [];
  if (n === 1) {
    pos[Number(alumnos[0].id)] = { x: cx, y: cy };
  } else {
    slots.forEach((s, i) => {
      const ang = start + (i * 2 * Math.PI) / m;
      if (s.node !== undefined) pos[s.node] = { x: cx + ringR * Math.cos(ang), y: cy + ringR * Math.sin(ang) };
      else seps.push(ang);
    });
  }

  const svgStyle = ajustar
    ? { width: "100%", height: "100%", display: "block", touchAction: "manipulation" }
    : { width: "100%", height: "auto", display: "block", touchAction: "manipulation" };

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} preserveAspectRatio="xMidYMid meet" style={svgStyle}>
      <defs>
        <filter id="afShadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#0f172a" floodOpacity="0.18" />
        </filter>
        <filter id="afGlow" x="-80%" y="-80%" width="260%" height="260%">
          <feDropShadow dx="0" dy="0" stdDeviation="3.5" floodColor="#f59e0b" floodOpacity="0.95" />
        </filter>
      </defs>

      {/* Separadores entre grupos */}
      {seps.map((ang, i) => {
        const r1 = ringR * 0.26, r2 = ringR + nodeR + 18;
        return (
          <line key={`sep_${i}`}
            x1={cx + r1 * Math.cos(ang)} y1={cy + r1 * Math.sin(ang)}
            x2={cx + r2 * Math.cos(ang)} y2={cy + r2 * Math.sin(ang)}
            stroke="#cbd5e1" strokeWidth={2.5} strokeDasharray="4 8" strokeLinecap="round" />
        );
      })}

      {/* Enlaces */}
      {[...links].map((k) => {
        const [a, b] = parseKey(k);
        const pa = pos[a], pb = pos[b];
        if (!pa || !pb) return null;
        const sup = supers.has(k);
        const nuevo = resaltarNuevos && resaltarNuevos.has(k);
        const mx = (pa.x + pb.x) / 2, my = (pa.y + pb.y) / 2;
        return (
          <g key={`edge_${k}`}>
            {nuevo && (
              <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={sup ? ORO.aro : "#16a34a"}
                strokeWidth={sup ? 22 : 15} strokeLinecap="round" opacity="0.2" />
            )}
            {sup ? (
              <>
                <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={ORO.borde} strokeWidth={13} strokeLinecap="round" />
                <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={ORO.linea} strokeWidth={8} strokeLinecap="round" />
              </>
            ) : (
              <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke="#94a3b8" strokeWidth={6} strokeLinecap="round" />
            )}
            {sup && (
              <g filter="url(#afGlow)">
                <circle cx={mx} cy={my} r={16} fill={ORO.circulo} stroke={ORO.borde} strokeWidth={2.5} />
                <text x={mx} y={my} textAnchor="middle" dominantBaseline="central" fontSize={19} fill="#fff" style={{ pointerEvents: "none" }}>★</text>
              </g>
            )}
            {onEdgeClick && (
              <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke="transparent" strokeWidth={28}
                strokeLinecap="round" style={{ cursor: "pointer" }} onClick={() => onEdgeClick(a, b)} />
            )}
          </g>
        );
      })}

      {/* Nodos */}
      {alumnos.map((al) => {
        const id = Number(al.id);
        const p = pos[id];
        if (!p) return null;
        const grado = grados[id] || 0;
        const c = colorNodo(grado, hayEnlaces);
        const gb = grupoBorde[id];
        const borde = gb || c.borde;
        const bw = gb ? 8 : 5;
        const esSuper = superNodos.has(id);
        const sel = selectedId === al.id;
        return (
          <g key={`node_${id}`} style={{ cursor: onNodeClick ? "pointer" : "default" }}
            onClick={onNodeClick ? () => onNodeClick(al.id) : undefined}>
            {esSuper && (
              <circle cx={p.x} cy={p.y} r={nodeR + 7} fill="none" stroke={ORO.aro} strokeWidth={5} filter="url(#afGlow)" />
            )}
            {sel && (
              <circle cx={p.x} cy={p.y} r={nodeR + 13} fill="none" stroke="#ec4899" strokeWidth={5} strokeDasharray="7 6" />
            )}
            <circle cx={p.x} cy={p.y} r={nodeR} fill={c.fill} stroke={borde} strokeWidth={bw} filter="url(#afShadow)" />
            <text x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central"
              fontSize={nodeR * 0.82} fontWeight="800" fill={c.texto} style={{ pointerEvents: "none" }}>
              {inicial(al.nombre)}
            </text>
            <text x={p.x} y={p.y + nodeR + 22} textAnchor="middle"
              fontSize="16" fontWeight="700" fill="#334155" style={{ pointerEvents: "none" }}>
              {corto(al.nombre)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/* ================================================================== */
/* Barra de progreso                                                  */
/* ================================================================== */

const BarraProgreso = ({ pasos, paso, onJump }) => {
  const total = pasos.length - 1;
  const pct = total > 0 ? (paso / total) * 100 : 0;
  return (
    <div style={S.barraWrap}>
      <div style={S.barraTrack}>
        <div style={{ ...S.barraFill, width: `${pct}%` }} />
        {pasos.map((s, i) => {
          const left = total > 0 ? (i / total) * 100 : 0;
          const on = i <= paso, actual = i === paso;
          return (
            <button key={i} onClick={() => onJump(i)} title={i === 0 ? "Inicio" : s.etiqueta}
              style={{ ...S.barraTick, left: `${left}%`,
                background: on ? "#ec4899" : "#e2e8f0", borderColor: on ? "#db2777" : "#cbd5e1",
                ...(actual ? S.barraTickActual : {}) }} />
          );
        })}
      </div>
    </div>
  );
};

/* ================================================================== */
/* Componente principal                                               */
/* ================================================================== */

const VELOCIDADES = [
  { etq: "0.5×", ms: 1800 },
  { etq: "1×", ms: 1000 },
  { etq: "2×", ms: 500 },
];

const Afinidad = ({ piso, onClose }) => {
  const draftKey = `afinidad_draft_${piso?.id}`;

  const [loading, setLoading] = useState(true);
  const [alumnos, setAlumnos] = useState([]);
  const [inicial, setInicial] = useState(new Set());
  const [inicialSupers, setInicialSupers] = useState(new Set());
  const [links, setLinks] = useState(new Set());
  const [supers, setSupers] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [edgeSel, setEdgeSel] = useState(null); // clave del enlace para el menú superlink/quitar
  const [vista, setVista] = useState("editor");
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmar, setConfirmar] = useState(false);

  const [pasos, setPasos] = useState([]);
  const [paso, setPaso] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [velocidad, setVelocidad] = useState(1);
  const [histCargado, setHistCargado] = useState(false);
  const [histModo, setHistModo] = useState("replay");

  const listoRef = useRef(false);

  const nombreById = useMemo(() => {
    const m = {};
    alumnos.forEach((a) => { m[Number(a.id)] = a.nombre; });
    return m;
  }, [alumnos]);

  useEffect(() => {
    if (!piso?.id) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piso?.id]);

  const cargar = async () => {
    try {
      setLoading(true);
      listoRef.current = false;
      const res = await axios.get(`/api/afinidad/${piso.id}`);
      const als = Array.isArray(res.data?.alumnos) ? res.data.alumnos : [];
      const cons = Array.isArray(res.data?.conexiones) ? res.data.conexiones : [];
      setAlumnos(als);

      const base = new Set();
      const baseS = new Set();
      cons.forEach((c) => {
        const k = keyOf(c.id_alumno_1, c.id_alumno_2);
        base.add(k);
        if (Number(c.superlink) === 1) baseS.add(k);
      });
      setInicial(base);
      setInicialSupers(baseS);

      let startL = base, startS = baseS;
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const d = JSON.parse(raw);
          const valid = new Set(als.map((a) => String(a.id)));
          const ok = (k) => { const [a, b] = parseKey(k); return valid.has(String(a)) && valid.has(String(b)); };
          if (Array.isArray(d?.l)) startL = new Set(d.l.filter(ok));
          if (Array.isArray(d?.s)) startS = new Set(d.s.filter((k) => ok(k) && startL.has(k)));
        }
      } catch { /* */ }

      setLinks(startL);
      setSupers(startS);
      listoRef.current = true;
    } catch (e) {
      console.error("Error cargando afinidad:", e);
      setToast({ tipo: "error", msg: "No se pudieron cargar los datos del piso." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!listoRef.current) return;
    try { localStorage.setItem(draftKey, JSON.stringify({ l: [...links], s: [...supers] })); } catch { /* */ }
  }, [links, supers, draftKey]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // nº de cambios netos: altas/bajas + cambios de nivel en los que persisten
  const cambios = useMemo(() => {
    let n = symDiff(links, inicial);
    links.forEach((k) => {
      if (inicial.has(k) && (inicialSupers.has(k) !== supers.has(k))) n++;
    });
    return n;
  }, [links, supers, inicial, inicialSupers]);

  const onNodeClick = (id) => {
    if (selectedId == null) { setSelectedId(id); return; }
    if (selectedId === id) { setSelectedId(null); return; }
    const k = keyOf(selectedId, id);
    if (links.has(k)) { setSelectedId(null); setEdgeSel(k); } // ya unidos -> menú
    else { crearLink(selectedId, id); setSelectedId(null); }  // crear normal
  };

  const crearLink = (a, b) => {
    const k = keyOf(a, b);
    setLinks((p) => { const nx = new Set(p); nx.add(k); return nx; });
  };
  const quitarLink = (a, b) => {
    const k = keyOf(a, b);
    setLinks((p) => { const nx = new Set(p); nx.delete(k); return nx; });
    setSupers((p) => { const nx = new Set(p); nx.delete(k); return nx; });
  };
  const toggleSuper = (a, b) => {
    const k = keyOf(a, b);
    setSupers((p) => { const nx = new Set(p); if (nx.has(k)) nx.delete(k); else nx.add(k); return nx; });
  };

  const guardar = async () => {
    if (cambios === 0) return;
    try {
      setGuardando(true);
      const enlaces = [...links].map((k) => { const [a, b] = parseKey(k); return [a, b, supers.has(k) ? 1 : 0]; });
      const res = await axios.post(`/api/afinidad/${piso.id}/guardar`, { enlaces });
      const cons = Array.isArray(res.data?.conexiones) ? res.data.conexiones : [];
      const base = new Set(), baseS = new Set();
      cons.forEach((c) => { const k = keyOf(c.id_alumno_1, c.id_alumno_2); base.add(k); if (Number(c.superlink) === 1) baseS.add(k); });
      setInicial(base); setInicialSupers(baseS);
      setLinks(base); setSupers(baseS);
      setHistCargado(false);
      const nn = res.data?.cambios ?? 0;
      setToast({ tipo: "ok", msg: nn === 0 ? "Sin cambios que guardar." : `Guardado: ${nn} cambio${nn === 1 ? "" : "s"}.` });
    } catch (e) {
      console.error("Error guardando:", e);
      const msg = e?.response?.status === 401 ? "Sesión no válida. Vuelve a iniciar sesión." : "No se pudo guardar. Inténtalo de nuevo.";
      setToast({ tipo: "error", msg });
    } finally {
      setGuardando(false);
    }
  };

  const intentarCerrar = () => { if (cambios > 0) setConfirmar(true); else onClose?.(); };
  const descartar = () => {
    try { localStorage.removeItem(draftKey); } catch { /* */ }
    setLinks(new Set(inicial)); setSupers(new Set(inicialSupers));
    setConfirmar(false); onClose?.();
  };

  const abrirHistorial = async () => {
    setVista("historial"); setPlaying(false); setHistModo("replay");
    if (histCargado) return;
    try {
      const res = await axios.get(`/api/afinidad/${piso.id}/historial`);
      const filas = Array.isArray(res.data) ? res.data : [];
      const dias = []; const idx = {};
      filas.forEach((f) => {
        const dia = (f.hecho_at || "").slice(0, 10);
        if (idx[dia] === undefined) { idx[dia] = dias.length; dias.push({ dia, acciones: [] }); }
        dias[idx[dia]].acciones.push(f);
      });

      const snaps = [{ dia: null, etiqueta: "Inicio", links: new Set(), supers: new Set(), nuevos: new Set(), acciones: [] }];
      let aL = new Set(), aS = new Set();
      dias.forEach((g) => {
        const nL = new Set(aL), nS = new Set(aS), nuevos = new Set();
        g.acciones.forEach((f) => {
          const k = keyOf(f.id_alumno_1, f.id_alumno_2);
          const sup = Number(f.superlink) === 1;
          if (esEliminar(f.accion)) { nL.delete(k); nS.delete(k); }
          else if (esActualizar(f.accion)) { if (sup) nS.add(k); else nS.delete(k); }
          else { nL.add(k); nuevos.add(k); if (sup) nS.add(k); else nS.delete(k); }
        });
        snaps.push({ dia: g.dia, etiqueta: formatoDia(g.dia), links: nL, supers: nS, nuevos, acciones: g.acciones });
        aL = nL; aS = nS;
      });

      setPasos(snaps);
      setPaso(snaps.length - 1);
      setHistCargado(true);
    } catch (e) {
      console.error("Error cargando historial:", e);
      setToast({ tipo: "error", msg: "No se pudo cargar el historial." });
    }
  };

  useEffect(() => {
    if (vista !== "historial" || histModo !== "replay" || !playing || pasos.length === 0) return;
    if (paso >= pasos.length - 1) { setPlaying(false); return; }
    const id = setTimeout(() => setPaso((p) => Math.min(p + 1, pasos.length - 1)), VELOCIDADES[velocidad].ms);
    return () => clearTimeout(id);
  }, [playing, paso, velocidad, vista, histModo, pasos.length]);

  const togglePlay = () => {
    if (pasos.length <= 1) return;
    if (paso >= pasos.length - 1) setPaso(0);
    setPlaying((p) => !p);
  };
  const saltarA = (i) => { setPlaying(false); setPaso(i); };

  if (loading) {
    return (
      <div style={S.overlay} onClick={intentarCerrar}>
        <div style={S.modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: 56, textAlign: "center", fontSize: 20, color: "#64748b" }}>Cargando alumnos…</div>
        </div>
      </div>
    );
  }

  const pasoActual = pasos[paso] || { links: new Set(), supers: new Set(), nuevos: new Set(), etiqueta: "", acciones: [] };
  const autor = pasoActual.acciones?.[0]?.hecho_por;

  const histReplay = vista === "historial" && histModo === "replay" && pasos.length > 1;
  const contentStyle = histReplay ? S.contentFlex : S.content;

  // datos del menú de superlink
  let edgeMenu = null;
  if (edgeSel) {
    const [ea, eb] = parseKey(edgeSel);
    edgeMenu = { a: ea, b: eb, na: nombreById[ea] || "—", nb: nombreById[eb] || "—", esSuper: supers.has(edgeSel) };
  }

  return (
    <div style={S.overlay} onClick={intentarCerrar}>
      <style>{CSS}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>

        {/* Cabecera */}
        <div style={S.header}>
          <div>
            <div style={S.kicker}>Afinidad del piso</div>
            <h2 style={S.title}>{piso?.abreviatura || "—"}</h2>
          </div>
          <button onClick={intentarCerrar} style={S.btnX} aria-label="Cerrar">✕</button>
        </div>



        {/* Contenido */}
        <div style={contentStyle}>
          {alumnos.length === 0 ? (
            <p style={S.vacio}>No hay alumnos en este piso.</p>
          ) : vista === "editor" ? (
            <>
              <p style={{ ...S.hint, ...(selectedId != null ? S.hintActivo : {}) }}>
                {selectedId == null
                  ? "Toca dos alumnos para unirlos. Toca una línea (o dos ya unidos) para ⭐ superlink o quitar."
                  : "Toca otro alumno para unirlos · toca el mismo para cancelar."}
              </p>
              <Anillo alumnos={alumnos} links={links} supers={supers} selectedId={selectedId}
                onNodeClick={onNodeClick} onEdgeClick={(a, b) => setEdgeSel(keyOf(a, b))} />
              <button onClick={abrirHistorial} style={S.btnHistorialGrande}>🕑 Ver historial</button>
              <Leyenda />
            </>
          ) : pasos.length <= 1 ? (
            <p style={S.vacio}>Todavía no hay movimientos registrados.</p>
          ) : histModo === "lista" ? (
            <div style={S.listaWrap}>
              <button onClick={() => setHistModo("replay")} style={S.btnVolverLista}>← Volver a la reproducción</button>
              {pasos.slice(1).map((s, i) => {
                const allAg = s.acciones.filter((a) => esCrear(a.accion));
                const allEl = s.acciones.filter((a) => esEliminar(a.accion));
                const acAll = s.acciones.filter((a) => esActualizar(a.accion));
                const agKeys = new Set(allAg.map((a) => keyOf(a.id_alumno_1, a.id_alumno_2)));
                const elKeys = new Set(allEl.map((a) => keyOf(a.id_alumno_1, a.id_alumno_2)));
                const cancelados = new Set([...agKeys].filter((k) => elKeys.has(k)));
                // Deduplicar: mismo par añadido varias veces → mostrar solo una vez
                const agSeen = new Set();
                const ag = allAg.filter((a) => { const k = keyOf(a.id_alumno_1, a.id_alumno_2); if (cancelados.has(k) || agSeen.has(k)) return false; agSeen.add(k); return true; });
                const elSeen = new Set();
                const el = allEl.filter((a) => { const k = keyOf(a.id_alumno_1, a.id_alumno_2); if (cancelados.has(k) || elSeen.has(k)) return false; elSeen.add(k); return true; });
                // Para superlink, conservar solo el último estado por par
                const acMap = new Map(); acAll.forEach((a) => acMap.set(keyOf(a.id_alumno_1, a.id_alumno_2), a));
                const ac = [...acMap.values()];
                const aut = s.acciones?.[0]?.hecho_por;
                if (ag.length === 0 && el.length === 0 && ac.length === 0) return null;
                return (
                  <div key={i} style={S.movCard}>
                    <div style={S.movHead}>
                      <span style={S.movNum}>Movimiento {i + 1}</span>
                      <span style={S.movFecha}>{s.etiqueta}{aut ? ` · ${aut}` : ""}</span>
                    </div>
                    {ag.length > 0 && (
                      <div style={S.movGrupo}>
                        <span style={{ ...S.movGrupoTit, color: "#15803d" }}>＋ Añadidos</span>
                        <div style={S.movChips}>{ag.map((a, j) => <span key={j} style={{ ...S.chip, ...S.chipCrear }}>＋ {a.nombre_1} ↔ {a.nombre_2}{Number(a.superlink) === 1 ? " ⭐" : ""}</span>)}</div>
                      </div>
                    )}
                    {ac.length > 0 && (
                      <div style={S.movGrupo}>
                        <span style={{ ...S.movGrupoTit, color: "#b45309" }}>⭐ Superlink</span>
                        <div style={S.movChips}>{ac.map((a, j) => <span key={j} style={{ ...S.chip, ...S.chipSuper }}>{Number(a.superlink) === 1 ? "★ marcado" : "☆ quitado"}: {a.nombre_1} ↔ {a.nombre_2}</span>)}</div>
                      </div>
                    )}
                    {el.length > 0 && (
                      <div style={S.movGrupo}>
                        <span style={{ ...S.movGrupoTit, color: "#b91c1c" }}>✕ Eliminados</span>
                        <div style={S.movChips}>{el.map((a, j) => <span key={j} style={{ ...S.chip, ...S.chipEliminar }}>－ {a.nombre_1} ↔ {a.nombre_2}</span>)}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div style={S.histEtiqueta}>
                <span style={S.histPaso}>{paso === 0 ? "Estado inicial" : `Movimiento ${paso} de ${pasos.length - 1}`}</span>
                <span style={S.histFecha}>{paso === 0 ? "Punto de partida" : pasoActual.etiqueta}{autor ? ` · ${autor}` : ""}</span>
              </div>

              <BarraProgreso pasos={pasos} paso={paso} onJump={saltarA} />

              <div style={S.ringBox}>
                <Anillo key={`h${paso}`} ajustar alumnos={alumnos}
                  links={pasoActual.links} supers={pasoActual.supers} resaltarNuevos={pasoActual.nuevos} />
              </div>

              <div style={S.barraControles}>
                <div style={S.controles}>
                  <button style={S.ctrlBtn} onClick={() => saltarA(0)} aria-label="Inicio">⏮</button>
                  <button style={S.ctrlBtn} onClick={() => saltarA(Math.max(0, paso - 1))} aria-label="Anterior">◀</button>
                  <button style={{ ...S.ctrlBtn, ...S.ctrlPlay }} onClick={togglePlay} aria-label="Reproducir">{playing ? "⏸" : "▶"}</button>
                  <button style={S.ctrlBtn} onClick={() => saltarA(Math.min(pasos.length - 1, paso + 1))} aria-label="Siguiente">▶</button>
                  <button style={S.ctrlBtn} onClick={() => saltarA(pasos.length - 1)} aria-label="Final">⏭</button>
                </div>
                <div style={S.velocidades}>
                  {VELOCIDADES.map((v, i) => (
                    <button key={v.etq} onClick={() => setVelocidad(i)}
                      style={{ ...S.velBtn, ...(velocidad === i ? S.velActiva : {}) }}>{v.etq}</button>
                  ))}
                </div>
                <button style={S.btnVerTodos} onClick={() => { setPlaying(false); setHistModo("lista"); }}>📋 Ver todos</button>
              </div>
            </>
          )}
        </div>

        {/* Pie */}
        <div style={S.footer}>
          {vista === "editor" ? (
            <>
              <button onClick={intentarCerrar} style={S.btnCancelar}>Cancelar</button>
              <button onClick={guardar} disabled={cambios === 0 || guardando}
                style={{ ...S.btnGuardar, ...(cambios === 0 || guardando ? S.btnDisabled : {}) }}>
                {guardando ? "Guardando…" : `Guardar cambios${cambios > 0 ? ` (${cambios})` : ""}`}
              </button>
            </>
          ) : (
            <button onClick={() => { setVista("editor"); setPlaying(false); }} style={S.btnVolver}>← Volver a editar</button>
          )}
        </div>

        {/* Menú de enlace (superlink / quitar) */}
        {edgeMenu && (
          <div style={S.confirmOverlay} onClick={() => setEdgeSel(null)}>
            <div style={S.confirmCard} onClick={(e) => e.stopPropagation()}>
              <h3 style={S.confirmTitle}>{edgeMenu.na} ↔ {edgeMenu.nb}</h3>
              <p style={S.confirmMsg}>
                {edgeMenu.esSuper ? "Este enlace está marcado como superlink ⭐." : "Enlace de afinidad normal."}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <button style={S.btnSuper}
                  onClick={() => { toggleSuper(edgeMenu.a, edgeMenu.b); setEdgeSel(null); }}>
                  {edgeMenu.esSuper ? "☆ Quitar superlink" : "⭐ Marcar como superlink"}
                </button>
                <button style={S.btnQuitarEnlace}
                  onClick={() => { quitarLink(edgeMenu.a, edgeMenu.b); setEdgeSel(null); }}>
                  🗑 Quitar enlace
                </button>
                <button style={S.btnSeguir} onClick={() => setEdgeSel(null)}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmación de cancelar */}
        {confirmar && (
          <div style={S.confirmOverlay} onClick={() => setConfirmar(false)}>
            <div style={S.confirmCard} onClick={(e) => e.stopPropagation()}>
              <h3 style={S.confirmTitle}>¿Descartar los cambios?</h3>
              <p style={S.confirmMsg}>
                Tienes {cambios} cambio{cambios === 1 ? "" : "s"} sin guardar. Si cancelas, se perderá{cambios === 1 ? "" : "n"}.
              </p>
              <div style={S.confirmBtns}>
                <button style={S.btnSeguir} onClick={() => setConfirmar(false)}>Seguir editando</button>
                <button style={S.btnDescartar} onClick={descartar}>Sí, descartar</button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && <div style={{ ...S.toast, ...(toast.tipo === "error" ? S.toastError : S.toastOk) }}>{toast.msg}</div>}
      </div>
    </div>
  );
};

/* ================================================================== */
/* Sub-componentes                                                    */
/* ================================================================== */

const Leyenda = () => (
  <div style={S.leyenda}>
    <Punto color="#9ca3af" txt="Sin enlaces" />
    <Punto color="#ef4444" txt="Aislado" />
    <Punto color="#86efac" txt="1" />
    <Punto color="#22c55e" txt="2" />
    <Punto color="#15803d" txt="3+" />
    <span style={S.leyendaItem}><span style={{ width: 18, height: 18, borderRadius: "50%", background: "transparent", border: "4px solid #f97316", display: "inline-block" }} />Grupo 1</span>
    <span style={S.leyendaItem}><span style={{ width: 18, height: 18, borderRadius: "50%", background: "transparent", border: "4px solid #eab308", display: "inline-block" }} />Grupo 2</span>
    <span style={S.leyendaItem}><span style={{ width: 19, height: 19, borderRadius: "50%", background: "#f59e0b", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, boxShadow: "0 0 6px rgba(245,158,11,0.8)" }}>★</span>Superlink</span>
  </div>
);

const Punto = ({ color, txt }) => (
  <span style={S.leyendaItem}><span style={{ ...S.leyendaPunto, background: color }} />{txt}</span>
);

const formatoDia = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const CSS = `
@keyframes af_pop { 0% { transform: scale(0.96); } 60% { transform: scale(1.02); } 100% { transform: scale(1); } }
@keyframes af_toast_in { from { opacity: 0; transform: translate(-50%, 12px); } to { opacity: 1; transform: translate(-50%, 0); } }
`;

/* ================================================================== */
/* Estilos                                                            */
/* ================================================================== */

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16, backdropFilter: "blur(3px)" },
  modal: { position: "relative", background: "#ffffff", borderRadius: 28, boxShadow: "0 30px 80px rgba(15,23,42,0.4)", maxWidth: 620, width: "100%", height: "94vh", maxHeight: "94vh", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", animation: "af_pop 0.18s ease-out" },
  header: { padding: "22px 28px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0, background: "linear-gradient(135deg, #ec4899, #f472b6)", color: "#fff" },
  kicker: { fontSize: 14, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", opacity: 0.92 },
  title: { margin: "4px 0 0", fontSize: 32, fontWeight: 800, lineHeight: 1.05 },
  btnX: { background: "rgba(255,255,255,0.22)", border: "none", color: "#fff", fontSize: 22, fontWeight: 700, width: 46, height: 46, borderRadius: "50%", cursor: "pointer", flexShrink: 0, lineHeight: 1 },
  tabs: { display: "flex", gap: 10, padding: "14px 24px 0", flexShrink: 0 },
  tab: { flex: 1, padding: "14px 0", fontSize: 18, fontWeight: 700, cursor: "pointer", border: "none", background: "transparent", color: "#94a3b8", borderBottom: "4px solid transparent", borderRadius: "8px 8px 0 0" },
  tabActiva: { color: "#ec4899", borderBottom: "4px solid #ec4899" },
  content: { padding: "16px 28px", flex: 1, overflowY: "auto" },
  contentFlex: { padding: "12px 24px 10px", flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" },
  vacio: { textAlign: "center", color: "#94a3b8", fontSize: 19, padding: 40 },
  hint: { textAlign: "center", fontSize: 16, color: "#475569", margin: "6px 0 14px", minHeight: 46, fontWeight: 600, lineHeight: 1.4 },
  hintActivo: { color: "#be185d", background: "#fce7f3", borderRadius: 12, padding: "10px 14px" },
  leyenda: { display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", padding: "16px 0 8px", marginTop: 8, borderTop: "1px solid #f1f5f9" },
  leyendaItem: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 15, fontWeight: 700, color: "#475569" },
  leyendaPunto: { width: 18, height: 18, borderRadius: "50%", display: "inline-block", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" },
  histEtiqueta: { textAlign: "center", flexShrink: 0 },
  histPaso: { display: "block", fontSize: 14, fontWeight: 800, color: "#ec4899", textTransform: "uppercase", letterSpacing: 0.8 },
  histFecha: { display: "block", fontSize: 19, fontWeight: 800, color: "#1e293b", textTransform: "capitalize" },
  barraWrap: { padding: "2px 6px", flexShrink: 0 },
  barraTrack: { position: "relative", height: 10, background: "#e2e8f0", borderRadius: 999, margin: "16px 10px 10px" },
  barraFill: { position: "absolute", left: 0, top: 0, height: "100%", background: "linear-gradient(90deg, #f472b6, #ec4899)", borderRadius: 999, transition: "width 0.3s ease" },
  barraTick: { position: "absolute", top: "50%", width: 18, height: 18, borderRadius: "50%", border: "3px solid", transform: "translate(-50%, -50%)", cursor: "pointer", padding: 0, transition: "all 0.2s ease" },
  barraTickActual: { width: 26, height: 26, boxShadow: "0 0 0 5px rgba(236,72,153,0.25)", zIndex: 2 },
  ringBox: { flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" },
  resumen: { display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", flexShrink: 0, maxHeight: 72, overflow: "hidden", margin: "4px 0" },
  resPill: { fontSize: 14, fontWeight: 700, padding: "7px 14px", borderRadius: 999 },
  barraControles: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", alignItems: "center", flexShrink: 0, marginTop: 8 },
  controles: { display: "flex", justifyContent: "center", alignItems: "center", gap: 10 },
  ctrlBtn: { width: 50, height: 50, borderRadius: 14, border: "2px solid #e2e8f0", background: "#f8fafc", fontSize: 20, cursor: "pointer", color: "#334155", fontWeight: 700 },
  ctrlPlay: { background: "#ec4899", color: "#fff", border: "2px solid #db2777", width: 62, height: 62, fontSize: 26, borderRadius: 18, boxShadow: "0 6px 18px rgba(236,72,153,0.4)" },
  velocidades: { display: "flex", gap: 8 },
  velBtn: { padding: "9px 16px", borderRadius: 999, border: "2px solid #e2e8f0", background: "#fff", fontSize: 16, fontWeight: 800, color: "#64748b", cursor: "pointer" },
  velActiva: { background: "#1e293b", color: "#fff", border: "2px solid #1e293b" },
  btnVerTodos: { padding: "11px 18px", borderRadius: 14, border: "2px solid #ec4899", background: "#fff", color: "#ec4899", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  btnHistorialGrande: { display: "block", width: "100%", marginTop: 14, padding: "14px 0", borderRadius: 16, background: "linear-gradient(135deg, #ec4899, #f472b6)", color: "#fff", border: "none", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 18px rgba(236,72,153,0.3)", letterSpacing: 0.3 },
  listaWrap: { display: "flex", flexDirection: "column", gap: 14 },
  btnVolverLista: { display: "block", width: "100%", marginBottom: 14, padding: "14px 0", borderRadius: 16, background: "linear-gradient(135deg, #6366f1, #818cf8)", color: "#fff", border: "none", fontSize: 17, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 14px rgba(99,102,241,0.3)" },
  movCard: { border: "1px solid #f1f5f9", borderRadius: 16, padding: "14px 16px", background: "#fcfcfd", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  movHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, gap: 8, flexWrap: "wrap" },
  movNum: { fontSize: 15, fontWeight: 800, color: "#ec4899", textTransform: "uppercase", letterSpacing: 0.5 },
  movFecha: { fontSize: 15, fontWeight: 700, color: "#64748b", textTransform: "capitalize" },
  movGrupo: { marginTop: 8 },
  movGrupoTit: { display: "block", fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 },
  movChips: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: { fontSize: 15, fontWeight: 700, padding: "8px 14px", borderRadius: 999 },
  chipCrear: { background: "#dcfce7", color: "#15803d" },
  chipEliminar: { background: "#fee2e2", color: "#b91c1c" },
  chipSuper: { background: "#fef3c7", color: "#92400e" },
  footer: { padding: "16px 28px", borderTop: "1px solid #f1f5f9", flexShrink: 0, display: "flex", gap: 14, justifyContent: "flex-end", background: "#fff" },
  btnCancelar: { padding: "16px 28px", borderRadius: 16, border: "2px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 18, fontWeight: 700, cursor: "pointer" },
  btnGuardar: { padding: "16px 32px", borderRadius: 16, border: "none", cursor: "pointer", background: "linear-gradient(135deg, #ec4899, #f472b6)", color: "#fff", fontSize: 18, fontWeight: 800, boxShadow: "0 8px 22px rgba(236,72,153,0.4)" },
  btnDisabled: { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none", cursor: "not-allowed" },
  btnVolver: { padding: "16px 32px", borderRadius: 16, border: "none", cursor: "pointer", background: "linear-gradient(135deg, #6366f1, #818cf8)", color: "#fff", fontSize: 18, fontWeight: 800, width: "100%", boxShadow: "0 6px 18px rgba(99,102,241,0.35)" },
  confirmOverlay: { position: "absolute", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 5 },
  confirmCard: { background: "#fff", borderRadius: 22, padding: 30, maxWidth: 400, width: "100%", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" },
  confirmTitle: { margin: "0 0 10px", fontSize: 24, fontWeight: 800, color: "#1e293b" },
  confirmMsg: { margin: "0 0 24px", fontSize: 17, color: "#64748b", lineHeight: 1.5 },
  confirmBtns: { display: "flex", gap: 14 },
  btnSeguir: { flex: 1, padding: "14px", borderRadius: 14, border: "2px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 17, fontWeight: 700, cursor: "pointer" },
  btnDescartar: { flex: 1, padding: "14px", borderRadius: 14, border: "none", background: "#ef4444", color: "#fff", fontSize: 17, fontWeight: 800, cursor: "pointer" },
  btnSuper: { padding: "16px", borderRadius: 14, border: "none", background: "linear-gradient(135deg, #f59e0b, #fbbf24)", color: "#fff", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 16px rgba(245,158,11,0.4)" },
  btnQuitarEnlace: { padding: "14px", borderRadius: 14, border: "2px solid #fecaca", background: "#fff", color: "#b91c1c", fontSize: 17, fontWeight: 700, cursor: "pointer" },
  toast: { position: "absolute", bottom: 100, left: "50%", transform: "translateX(-50%)", padding: "14px 26px", borderRadius: 14, fontSize: 17, fontWeight: 700, boxShadow: "0 10px 28px rgba(0,0,0,0.25)", zIndex: 6, maxWidth: "88%", textAlign: "center", animation: "af_toast_in 0.25s ease-out" },
  toastOk: { background: "#16a34a", color: "#fff" },
  toastError: { background: "#ef4444", color: "#fff" },
};

export default Afinidad;
